import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Check, X, Settings, Plus, Trash2, Truck, DollarSign, Crown, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { usePagination } from '../../hooks/usePagination';
import TablePagination from '../../components/ui/table-pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  zh: '中文',
};
const ROUNDING_MODES = ['floor', 'round', 'ceil', 'none'];
const PREVIEW_BASE_AMOUNT = 100000;

// Same formula as the backend: base / rate * (1 + pct/100), then rounded.
const computeReferencePrice = (base, rate, percent, rounding) => {
  const raw = (base / rate) * (1 + percent / 100);
  switch (rounding) {
    case 'floor': return Math.floor(raw);
    case 'ceil': return Math.ceil(raw);
    case 'none': return Math.round(raw * 100) / 100;
    default: return Math.round(raw);
  }
};
const formatAmount = (value, rounding) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: rounding === 'none' ? 2 : 0,
    maximumFractionDigits: rounding === 'none' ? 2 : 0,
  }).format(value);

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

const AdminLocationsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, enabled, disabled
  
  // Tax Config Dialog State
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [taxConfig, setTaxConfig] = useState({
    isEnabled: false,
    taxName: 'VAT',
    defaultRate: 0,
    includedInPrice: false,
    displayOnCheckout: true,
    rules: []
  });

  // Shipping Config Dialog State
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingConfig, setShippingConfig] = useState({
    freeShipping: false,
    freeShippingLabel: ''
  });

  // Pricing state
  const [pricingOptions, setPricingOptions] = useState({ languages: Object.keys(LANGUAGE_NAMES), currencies: {} });
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    currency: '', currencySymbol: '', language: 'en', mode: 'fixed',
    percent: '0', rate: '', rounding: 'round',
    autoRate: false, threshold: '1', rateUpdatedAt: null, rateSource: null,
  });
  const [saving, setSaving] = useState(false);
  const [baseTarget, setBaseTarget] = useState(null);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  // FX (automatic exchange rates) state
  const [fxStatus, setFxStatus] = useState(null);
  const [fxDialogOpen, setFxDialogOpen] = useState(false);
  const [fxResult, setFxResult] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxApplying, setFxApplying] = useState(false);
  const [fxForce, setFxForce] = useState(false);

  useEffect(() => {
    fetchCountries();
    adminApi.countries
      .getPricingOptions(localStorage.getItem('accessToken'))
      .then((opts) => setPricingOptions(opts))
      .catch((err) => console.error('Error fetching pricing options:', err));
    loadFxStatus();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [countries, searchQuery, filterStatus]);

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const data = await adminApi.countries.getAll(token);
      setCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load countries',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFxStatus = () =>
    adminApi.countries
      .getFxStatus(localStorage.getItem('accessToken'))
      .then((res) => setFxStatus(res?.lastRun || null))
      .catch((err) => console.error('Error fetching FX status:', err));

  const applyFilters = () => {
    let filtered = [...countries];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (country) =>
          country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          country.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          country.languageName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          country.currency.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus === 'enabled') {
      filtered = filtered.filter((country) => country.isEnabled);
    } else if (filterStatus === 'disabled') {
      filtered = filtered.filter((country) => !country.isEnabled);
    }

    setFilteredCountries(filtered);
  };

  // Pagination
  const {
    currentPage,
    totalPages,
    currentItems: paginatedCountries,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(filteredCountries, 20);

  const handleToggleStatus = async (countryId, currentStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      await adminApi.countries.update(countryId, { isEnabled: !currentStatus }, token);
      
      toast({
        title: 'Success',
        description: `Country ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
      });
      
      fetchCountries();
    } catch (error) {
      console.error('Error updating country:', error);
      toast({
        title: 'Error',
        description: 'Failed to update country status',
        variant: 'destructive',
      });
    }
  };

  const openTaxConfig = (country) => {
    setSelectedCountry(country);
    // Load existing tax config or use defaults
    if (country.taxConfig) {
      setTaxConfig(country.taxConfig);
    } else {
      setTaxConfig({
        isEnabled: false,
        taxName: 'VAT',
        defaultRate: 0,
        includedInPrice: false,
        displayOnCheckout: true,
        rules: []
      });
    }
    setTaxDialogOpen(true);
  };

  const handleSaveTaxConfig = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      await adminApi.countries.update(
        selectedCountry.id,
        { taxConfig },
        token
      );

      toast({
        title: 'Success',
        description: 'Tax configuration saved successfully',
      });

      setTaxDialogOpen(false);
      fetchCountries();
    } catch (error) {
      console.error('Error saving tax config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save tax configuration',
        variant: 'destructive',
      });
    }
  };

  const addTaxRule = () => {
    setTaxConfig({
      ...taxConfig,
      rules: [
        ...taxConfig.rules,
        { name: '', rate: 0, isActive: true }
      ]
    });
  };

  const updateTaxRule = (index, field, value) => {
    const newRules = [...taxConfig.rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setTaxConfig({ ...taxConfig, rules: newRules });
  };

  const removeTaxRule = (index) => {
    const newRules = taxConfig.rules.filter((_, i) => i !== index);
    setTaxConfig({ ...taxConfig, rules: newRules });
  };

  const openShippingConfig = (country) => {
    setSelectedCountry(country);
    if (country.shippingConfig) {
      setShippingConfig({
        freeShipping: !!country.shippingConfig.freeShipping,
        freeShippingLabel: country.shippingConfig.freeShippingLabel || ''
      });
    } else {
      setShippingConfig({ freeShipping: false, freeShippingLabel: '' });
    }
    setShippingDialogOpen(true);
  };

  const handleSaveShippingConfig = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      await adminApi.countries.update(
        selectedCountry.id,
        { shippingConfig },
        token
      );
      toast({
        title: 'Success',
        description: 'Shipping configuration saved successfully',
      });
      setShippingDialogOpen(false);
      fetchCountries();
    } catch (error) {
      console.error('Error saving shipping config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save shipping configuration',
        variant: 'destructive',
      });
    }
  };

  const baseCountry = countries.find((c) => c.isBase);
  const baseCurrency = baseCountry?.currency;

  const errorMessage = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.code === 'INVALID_PRICING') {
      const map = {
        no_base_country: 'adminPricing.errNoBase',
        exchange_rate_required: 'adminPricing.errRateRequired',
        percent_too_low: 'adminPricing.errPercentLow',
      };
      return (detail.problems || [])
        .map((pr) => (map[pr] ? t(map[pr]) : String(pr)))
        .join(' ');
    }
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && detail.message) return String(detail.message);
    return t('adminPricing.errGeneric');
  };

  const rebuildSummary = (rebuild) => {
    if (!rebuild) return '';
    return t('adminPricing.toastRebuilt', {
      count: rebuild.products ?? 0,
      versions: (rebuild.versionsCreated ?? 0) + (rebuild.versionsUpdated ?? 0),
    });
  };

  const openPricing = (country) => {
    setSelectedCountry(country);
    const pr = country.pricing || {};
    setPricingForm({
      currency: country.currency || '',
      currencySymbol: country.currencySymbol || '',
      language: country.language || 'en',
      mode: country.isBase ? 'fixed' : pr.mode === 'reference' ? 'reference' : 'fixed',
      percent: String(pr.referencePercent ?? 0),
      rate: pr.exchangeRate != null ? String(pr.exchangeRate) : '',
      rounding: pr.rounding || 'round',
      autoRate: !!pr.autoRate,
      threshold: String(pr.autoRateThresholdPercent ?? 1),
      rateUpdatedAt: pr.rateUpdatedAt || null,
      rateSource: pr.rateSource || null,
    });
    setPricingDialogOpen(true);
  };

  const sameCurrency = (code) => !!baseCurrency && code === baseCurrency;

  const pricingPercentValid =
    pricingForm.percent !== '' && Number.isFinite(Number(pricingForm.percent)) && Number(pricingForm.percent) > -100;
  const pricingRateNeeded =
    pricingForm.mode === 'reference' && !sameCurrency(pricingForm.currency);
  const pricingRateValid = !pricingRateNeeded || Number(pricingForm.rate) > 0;
  const autoRateActive = pricingRateNeeded && pricingForm.autoRate;
  const thresholdValid =
    pricingForm.threshold !== '' &&
    Number.isFinite(Number(pricingForm.threshold)) &&
    Number(pricingForm.threshold) >= 0 &&
    Number(pricingForm.threshold) <= 50;
  const pricingFormValid =
    pricingForm.currency.length === 3 &&
    (pricingForm.mode !== 'reference' ||
      (pricingPercentValid && pricingRateValid && (!autoRateActive || thresholdValid)));

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
  };
  const fxErrorText = (code) => {
    if (!code) return '';
    const map = {
      no_source_available: 'adminPricing.fxErr_no_source_available',
      no_base_country: 'adminPricing.fxErr_no_base_country',
    };
    return map[code] ? t(map[code]) : String(code);
  };
  const fxStatusLabel = (status) => {
    const known = ['apply', 'apply:single_source', 'within_threshold', 'needs_review', 'sources_disagree'];
    return known.includes(status)
      ? t(`adminPricing.fxStatus_${String(status).replace(':', '_')}`)
      : String(status || '');
  };
  const fxLastRunText = () => {
    if (!fxStatus) return t('adminPricing.fxLastRunNone');
    return t('adminPricing.fxLastRun', {
      date: formatDateTime(fxStatus.at),
      sources: (fxStatus.sources || []).join(', ') || '—',
    });
  };

  const handleOpenFx = async () => {
    setFxDialogOpen(true);
    setFxResult(null);
    setFxForce(false);
    setFxLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await adminApi.countries.refreshRates({ dryRun: true }, token);
      setFxResult(res);
    } catch (error) {
      console.error('Error previewing rates:', error);
      toast({ title: t('adminPricing.errorTitle'), description: errorMessage(error), variant: 'destructive' });
      setFxDialogOpen(false);
    } finally {
      setFxLoading(false);
    }
  };

  const handleApplyFx = async () => {
    setFxApplying(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await adminApi.countries.refreshRates({ dryRun: false, force: fxForce }, token);
      if (res?.error) {
        toast({ title: t('adminPricing.errorTitle'), description: fxErrorText(res.error), variant: 'destructive' });
      } else {
        const applied = (res?.results || []).filter(
          (r) => String(r.status).startsWith('apply') || (fxForce && r.status === 'needs_review')
        ).length;
        toast({
          title: t('adminPricing.fxToastTitle'),
          description: t('adminPricing.fxToastApplied', { count: applied, total: (res?.results || []).length }),
        });
        setFxDialogOpen(false);
        fetchCountries();
        loadFxStatus();
      }
    } catch (error) {
      console.error('Error applying rates:', error);
      toast({ title: t('adminPricing.errorTitle'), description: errorMessage(error), variant: 'destructive' });
    } finally {
      setFxApplying(false);
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const body = {
        currency: pricingForm.currency,
        currencySymbol: pricingForm.currencySymbol,
        language: pricingForm.language,
        languageName: LANGUAGE_NAMES[pricingForm.language] || pricingForm.language,
      };
      if (!selectedCountry.isBase) {
        body.pricing = {
          mode: pricingForm.mode,
          referencePercent: Number(pricingForm.percent) || 0,
          exchangeRate: pricingForm.rate === '' ? null : Number(pricingForm.rate),
          rounding: pricingForm.rounding,
          autoRate: autoRateActive,
          autoRateThresholdPercent: thresholdValid ? Number(pricingForm.threshold) : 1,
        };
      }
      const res = await adminApi.countries.update(selectedCountry.id, body, token);
      toast({
        title: t('adminPricing.toastSaved'),
        description: rebuildSummary(res?.rebuild) || undefined,
      });
      setPricingDialogOpen(false);
      fetchCountries();
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast({ title: t('adminPricing.errorTitle'), description: errorMessage(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleMakeBase = async () => {
    if (!baseTarget) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await adminApi.countries.update(baseTarget.id, { isBase: true }, token);
      toast({
        title: t('adminPricing.toastBaseChanged', { country: baseTarget.name }),
        description: rebuildSummary(res?.rebuild) || undefined,
      });
      setBaseTarget(null);
      fetchCountries();
    } catch (error) {
      console.error('Error setting base country:', error);
      toast({ title: t('adminPricing.errorTitle'), description: errorMessage(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRebuild = async (dryRun) => {
    setRebuilding(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await adminApi.countries.rebuildPrices({ dryRun: !!dryRun }, token);
      if (dryRun) {
        toast({
          title: t('adminPricing.toastPreviewTitle'),
          description: t('adminPricing.toastPreview', {
            count: res?.products ?? 0,
            changes: res?.priceChanges ?? 0,
          }),
        });
      } else {
        toast({ title: t('adminPricing.toastRebuiltTitle'), description: rebuildSummary(res) });
        setRebuildConfirmOpen(false);
        fetchCountries();
      }
    } catch (error) {
      console.error('Error rebuilding prices:', error);
      toast({ title: t('adminPricing.errorTitle'), description: errorMessage(error), variant: 'destructive' });
    } finally {
      setRebuilding(false);
    }
  };

  const pricingBadge = (country) => {
    if (country.isBase) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">
          <Crown className="h-3 w-3" />
          {t('adminPricing.baseBadge')}
        </span>
      );
    }
    const pr = country.pricing || {};
    if (pr.mode === 'reference') {
      const pct = Number(pr.referencePercent) || 0;
      const missingRate = !sameCurrency(country.currency) && !(Number(pr.exchangeRate) > 0);
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
            {t('adminPricing.referenceBadge', { percent: `${pct > 0 ? '+' : ''}${pct}` })}
            {pr.autoRate && !sameCurrency(country.currency) && ` · ${t('adminPricing.autoBadge')}`}
          </span>
          {missingRate && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="h-3 w-3" />
              {t('adminPricing.warnNoRate')}
            </span>
          )}
          {!baseCountry && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="h-3 w-3" />
              {t('adminPricing.warnNoBase')}
            </span>
          )}
        </div>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
        {t('adminPricing.fixedBadge')}
      </span>
    );
  };

  // Live preview for the pricing dialog
  const renderPreview = () => {
    if (!baseCurrency) return t('adminPricing.warnNoBase');
    if (selectedCountry?.isBase) return t('adminPricing.baseFixedNote');
    if (pricingForm.mode !== 'reference') return t('adminPricing.previewFixed');
    if (!pricingPercentValid) return t('adminPricing.previewUnavailable');
    const rate = sameCurrency(pricingForm.currency) ? 1 : Number(pricingForm.rate);
    if (!(rate > 0)) return t('adminPricing.previewUnavailable');
    const result = computeReferencePrice(PREVIEW_BASE_AMOUNT, rate, Number(pricingForm.percent), pricingForm.rounding);
    return t('adminPricing.previewLine', {
      base: `${formatAmount(PREVIEW_BASE_AMOUNT, 'round')} ${baseCurrency}`,
      result: `${formatAmount(result, pricingForm.rounding)} ${pricingForm.currency}`,
    });
  };

  const currencyEntries = (() => {
    const map = { ...(pricingOptions.currencies || {}) };
    if (pricingForm.currency && !map[pricingForm.currency]) {
      map[pricingForm.currency] = pricingForm.currencySymbol;
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  })();

  const enabledCount = countries.filter((c) => c.isEnabled).length;
  const disabledCount = countries.filter((c) => !c.isEnabled).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations Management</h1>
          <p className="text-gray-600 mt-1">
            {enabledCount} enabled · {disabledCount} disabled · {countries.length} total
          </p>
          <p className="text-xs text-gray-500 mt-1" data-testid="fx-last-run">{fxLastRunText()}</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            onClick={handleOpenFx}
            disabled={rebuilding || fxLoading || fxApplying}
            data-testid="fx-refresh-btn"
          >
            {fxLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            {t('adminPricing.fxRefresh')}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleRebuild(true)}
            disabled={rebuilding}
            data-testid="pricing-preview-btn"
          >
            {t('adminPricing.previewRebuild')}
          </Button>
          <Button
            onClick={() => setRebuildConfirmOpen(true)}
            disabled={rebuilding}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
            data-testid="pricing-rebuild-all-btn"
          >
            {rebuilding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            {t('adminPricing.rebuildAll')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search by country, language, or currency..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('all')}
              className={filterStatus === 'all' ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
            >
              All ({countries.length})
            </Button>
            <Button
              variant={filterStatus === 'enabled' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('enabled')}
              className={filterStatus === 'enabled' ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
            >
              Enabled ({enabledCount})
            </Button>
            <Button
              variant={filterStatus === 'disabled' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('disabled')}
              className={filterStatus === 'disabled' ? 'bg-[#be1f1a] hover:bg-[#a71b17]' : ''}
            >
              Disabled ({disabledCount})
            </Button>
          </div>
        </div>
      </div>

      {/* Countries Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Status</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead className="text-center">{t('adminPricing.colPricing')}</TableHead>
              <TableHead className="text-center">Tax</TableHead>
              <TableHead className="text-center">Shipping</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCountries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                  No countries found
                </TableCell>
              </TableRow>
            ) : (
              paginatedCountries.map((country) => (
                <TableRow key={country.id}>
                  <TableCell>
                    {country.isEnabled ? (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                        <X className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      {country.name}
                      {country.isBase && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-semibold uppercase">
                          <Crown className="h-3 w-3" />
                          {t('adminPricing.baseBadge')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{country.code}</TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{country.languageName}</span>
                    <span className="text-xs text-gray-400 ml-2">({country.language})</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{country.currency}</span>
                    <span className="text-gray-400 ml-2">{country.currencySymbol}</span>
                  </TableCell>
                  <TableCell className="text-center">{pricingBadge(country)}</TableCell>
                  <TableCell className="text-center">
                    {country.taxConfig?.isEnabled ? (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        <Check className="h-3 w-3" />
                        {country.taxConfig.defaultRate}%
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Not configured</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {country.shippingConfig?.freeShipping ? (
                      <div
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                        data-testid={`shipping-free-badge-${country.code}`}
                      >
                        <Truck className="h-3 w-3" />
                        Free
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Carrier / manual</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPricing(country)}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        data-testid={`pricing-config-btn-${country.code}`}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        {t('adminPricing.pricingBtn')}
                      </Button>
                      {!country.isBase && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setBaseTarget(country)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`make-base-btn-${country.code}`}
                        >
                          <Crown className="h-4 w-4 mr-1" />
                          {t('adminPricing.makeBase')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openShippingConfig(country)}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        data-testid={`shipping-config-btn-${country.code}`}
                      >
                        <Truck className="h-4 w-4 mr-1" />
                        Shipping
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTaxConfig(country)}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Tax Config
                      </Button>
                      <Button
                        variant={country.isEnabled ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => handleToggleStatus(country.id, country.isEnabled)}
                        disabled={country.isBase && country.isEnabled}
                        title={country.isBase && country.isEnabled ? t('adminPricing.cannotDisableBase') : undefined}
                        className={
                          country.isEnabled
                            ? 'border-red-300 text-red-600 hover:bg-red-50'
                            : 'bg-[#be1f1a] hover:bg-[#a71b17] text-white'
                        }
                      >
                        {country.isEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={goToPage}
        />
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          {t('adminPricing.infoNote')}
        </p>
      </div>

      {/* Tax Configuration Dialog */}
      <Dialog open={taxDialogOpen} onOpenChange={setTaxDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tax Configuration - {selectedCountry?.name}</DialogTitle>
            <DialogDescription>
              Configure tax settings and rules for {selectedCountry?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Enable Tax */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Enable Tax Collection</Label>
                <p className="text-sm text-gray-500">Apply taxes to orders in this country</p>
              </div>
              <Switch
                checked={taxConfig.isEnabled}
                onCheckedChange={(checked) => setTaxConfig({ ...taxConfig, isEnabled: checked })}
              />
            </div>

            {taxConfig.isEnabled && (
              <>
                {/* Tax Name */}
                <div className="grid gap-2">
                  <Label htmlFor="taxName">Tax Display Name *</Label>
                  <Input
                    id="taxName"
                    value={taxConfig.taxName}
                    onChange={(e) => setTaxConfig({ ...taxConfig, taxName: e.target.value })}
                    placeholder="VAT, GST, Sales Tax, etc."
                  />
                  <p className="text-xs text-gray-500">
                    This name will be displayed to customers (e.g., "VAT", "GST", "Sales Tax")
                  </p>
                </div>

                {/* Default Tax Rate */}
                <div className="grid gap-2">
                  <Label htmlFor="defaultRate">Default Tax Rate (%) *</Label>
                  <Input
                    id="defaultRate"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxConfig.defaultRate}
                    onChange={(e) => setTaxConfig({ ...taxConfig, defaultRate: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500">
                    The standard tax rate applied to most products
                  </p>
                </div>

                {/* Tax Display Options */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-medium">Display Options</h3>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Tax Included in Price</Label>
                      <p className="text-sm text-gray-500">Prices displayed include tax (common in EU)</p>
                    </div>
                    <Switch
                      checked={taxConfig.includedInPrice}
                      onCheckedChange={(checked) => setTaxConfig({ ...taxConfig, includedInPrice: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Tax Breakdown</Label>
                      <p className="text-sm text-gray-500">Display tax details on checkout</p>
                    </div>
                    <Switch
                      checked={taxConfig.displayOnCheckout}
                      onCheckedChange={(checked) => setTaxConfig({ ...taxConfig, displayOnCheckout: checked })}
                    />
                  </div>
                </div>

                {/* Special Tax Rules */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">Special Tax Rules</h3>
                      <p className="text-sm text-gray-500">Define custom tax rates for specific categories</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTaxRule}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Rule
                    </Button>
                  </div>

                  {taxConfig.rules.length > 0 && (
                    <div className="space-y-3">
                      {taxConfig.rules.map((rule, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 border rounded-lg">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Rule Name</Label>
                              <Input
                                value={rule.name}
                                onChange={(e) => updateTaxRule(index, 'name', e.target.value)}
                                placeholder="e.g., Food & Beverages"
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Tax Rate (%)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={rule.rate}
                                onChange={(e) => updateTaxRule(index, 'rate', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            <Switch
                              checked={rule.isActive}
                              onCheckedChange={(checked) => updateTaxRule(index, 'isActive', checked)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTaxRule(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {taxConfig.rules.length === 0 && (
                    <div className="text-center py-6 text-sm text-gray-500 border-2 border-dashed rounded-lg">
                      No special tax rules defined. The default rate will apply to all products.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => setTaxDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTaxConfig}
                className="bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                Save Tax Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shipping Configuration Dialog */}
      <Dialog open={shippingDialogOpen} onOpenChange={setShippingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Shipping Configuration - {selectedCountry?.name}</DialogTitle>
            <DialogDescription>
              Control how shipping cost is calculated when the destination address is in {selectedCountry?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Free shipping for this location</Label>
                <p className="text-sm text-gray-500">
                  Skip the carrier rate lookup and charge $0 for any order shipping here.
                </p>
              </div>
              <Switch
                data-testid="shipping-free-toggle"
                checked={shippingConfig.freeShipping}
                onCheckedChange={(checked) =>
                  setShippingConfig({ ...shippingConfig, freeShipping: checked })
                }
              />
            </div>

            {shippingConfig.freeShipping && (
              <div className="grid gap-2">
                <Label htmlFor="freeShippingLabel">Label shown to the customer (optional)</Label>
                <Input
                  id="freeShippingLabel"
                  data-testid="shipping-free-label"
                  value={shippingConfig.freeShippingLabel}
                  onChange={(e) =>
                    setShippingConfig({ ...shippingConfig, freeShippingLabel: e.target.value })
                  }
                  placeholder="Envío Gratis"
                />
                <p className="text-xs text-gray-500">
                  Defaults to &quot;Envío Gratis&quot; (Spanish locations) / &quot;Free Shipping&quot; (others).
                </p>
              </div>
            )}

            {!shippingConfig.freeShipping && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                Carrier lookup will be attempted (FedEx / Envía when API credentials are set in
                Admin → Settings → Access). Falls back to manual rates from{' '}
                <code>shipping_options</code> if no carrier is configured.
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setShippingDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="shipping-save-btn"
                onClick={handleSaveShippingConfig}
                className="bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                Save Shipping Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={(o) => !saving && setPricingDialogOpen(o)}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('adminPricing.dialogTitle', { country: selectedCountry?.name })}</DialogTitle>
            <DialogDescription>{t('adminPricing.dialogDesc')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="grid gap-2 sm:col-span-1">
                <Label htmlFor="pricing-currency">{t('adminPricing.currency')}</Label>
                <select
                  id="pricing-currency"
                  className={selectClass}
                  value={pricingForm.currency}
                  onChange={(e) => {
                    const code = e.target.value;
                    setPricingForm({
                      ...pricingForm,
                      currency: code,
                      currencySymbol: pricingOptions.currencies?.[code] ?? pricingForm.currencySymbol,
                    });
                  }}
                >
                  {currencyEntries.map(([code, sym]) => (
                    <option key={code} value={code}>{code} ({sym})</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pricing-symbol">{t('adminPricing.currencySymbol')}</Label>
                <Input
                  id="pricing-symbol"
                  value={pricingForm.currencySymbol}
                  maxLength={5}
                  onChange={(e) => setPricingForm({ ...pricingForm, currencySymbol: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pricing-language">{t('adminPricing.language')}</Label>
                <select
                  id="pricing-language"
                  className={selectClass}
                  value={pricingForm.language}
                  onChange={(e) => setPricingForm({ ...pricingForm, language: e.target.value })}
                >
                  {(pricingOptions.languages || Object.keys(LANGUAGE_NAMES)).map((code) => (
                    <option key={code} value={code}>{LANGUAGE_NAMES[code] || code}</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedCountry?.isBase ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                {t('adminPricing.baseFixedNote')}
              </div>
            ) : (
              <>
                <div className="grid gap-2 border-t pt-4">
                  <Label>{t('adminPricing.mode')}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {['fixed', 'reference'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPricingForm({ ...pricingForm, mode: m })}
                        className={`text-left rounded-lg border p-3 transition-colors ${
                          pricingForm.mode === m
                            ? 'border-[#be1f1a] bg-red-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`pricing-mode-${m}`}
                      >
                        <div className="font-medium text-sm">
                          {m === 'fixed' ? t('adminPricing.modeFixed') : t('adminPricing.modeReference')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {m === 'fixed' ? t('adminPricing.modeFixedHint') : t('adminPricing.modeReferenceHint')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {pricingForm.mode === 'reference' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="pricing-percent">{t('adminPricing.percent')}</Label>
                        <Input
                          id="pricing-percent"
                          type="number"
                          step="any"
                          value={pricingForm.percent}
                          onChange={(e) => setPricingForm({ ...pricingForm, percent: e.target.value })}
                          data-testid="pricing-percent"
                        />
                        <p className={`text-xs ${pricingPercentValid ? 'text-gray-500' : 'text-red-600'}`}>
                          {pricingPercentValid ? t('adminPricing.percentHelp') : t('adminPricing.errPercentLow')}
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="pricing-rounding">{t('adminPricing.rounding')}</Label>
                        <select
                          id="pricing-rounding"
                          className={selectClass}
                          value={pricingForm.rounding}
                          onChange={(e) => setPricingForm({ ...pricingForm, rounding: e.target.value })}
                        >
                          {ROUNDING_MODES.map((r) => (
                            <option key={r} value={r}>
                              {t(`adminPricing.rounding_${r}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="pricing-rate">{t('adminPricing.exchangeRate')}</Label>
                      {sameCurrency(pricingForm.currency) ? (
                        <p className="text-sm text-gray-600 bg-gray-50 border rounded-md p-3">
                          {t('adminPricing.exchangeSameCurrency', { currency: baseCurrency })}
                        </p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                            <div>
                              <Label htmlFor="pricing-auto-rate">{t('adminPricing.autoRate')}</Label>
                              <p className="text-xs text-gray-500">{t('adminPricing.autoRateHelp')}</p>
                            </div>
                            <Switch
                              id="pricing-auto-rate"
                              checked={pricingForm.autoRate}
                              onCheckedChange={(checked) => setPricingForm({ ...pricingForm, autoRate: checked })}
                              data-testid="pricing-auto-rate"
                            />
                          </div>
                          {pricingForm.autoRate && (
                            <div className="grid gap-2">
                              <Label htmlFor="pricing-threshold">{t('adminPricing.autoThreshold')}</Label>
                              <Input
                                id="pricing-threshold"
                                type="number"
                                step="any"
                                min="0"
                                max="50"
                                value={pricingForm.threshold}
                                onChange={(e) => setPricingForm({ ...pricingForm, threshold: e.target.value })}
                                data-testid="pricing-threshold"
                              />
                              <p className={`text-xs ${thresholdValid ? 'text-gray-500' : 'text-red-600'}`}>
                                {t('adminPricing.autoThresholdHelp')}
                              </p>
                            </div>
                          )}
                          <Input
                            id="pricing-rate"
                            type="number"
                            step="any"
                            min="0"
                            value={pricingForm.rate}
                            onChange={(e) => setPricingForm({ ...pricingForm, rate: e.target.value })}
                            readOnly={pricingForm.autoRate}
                            className={pricingForm.autoRate ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
                            placeholder="3304.90"
                            data-testid="pricing-rate"
                          />
                          {pricingForm.autoRate && (
                            <p className="text-xs text-gray-500" data-testid="pricing-rate-updated">
                              {t('adminPricing.autoLastUpdated', {
                                date: pricingForm.rateUpdatedAt
                                  ? formatDateTime(pricingForm.rateUpdatedAt)
                                  : t('adminPricing.autoNever'),
                                source: pricingForm.rateSource || t('adminPricing.autoNever'),
                              })}
                            </p>
                          )}
                          <p className={`text-xs ${pricingRateValid ? 'text-gray-500' : 'text-red-600'}`}>
                            {pricingRateValid
                              ? t('adminPricing.exchangeRateHelp', { base: baseCurrency || '?', currency: pricingForm.currency })
                              : t('adminPricing.errRateRequired')}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="rounded-lg border bg-gray-50 p-3" data-testid="pricing-preview">
              <div className="text-xs font-medium uppercase text-gray-500 mb-1">
                {t('adminPricing.previewTitle')}
              </div>
              <div className="text-sm font-medium text-gray-900">{renderPreview()}</div>
            </div>

            {saving && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                {t('adminPricing.savingHint')}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setPricingDialogOpen(false)} disabled={saving}>
                {t('adminPricing.cancel')}
              </Button>
              <Button
                onClick={handleSavePricing}
                disabled={saving || !pricingFormValid}
                className="bg-[#be1f1a] hover:bg-[#a71b17]"
                data-testid="pricing-save-btn"
              >
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {saving ? t('adminPricing.saving') : t('adminPricing.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Make base confirm */}
      <Dialog open={!!baseTarget} onOpenChange={(o) => !saving && !o && setBaseTarget(null)}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>{t('adminPricing.makeBaseTitle', { country: baseTarget?.name })}</DialogTitle>
            <DialogDescription>
              {t('adminPricing.makeBaseDesc', { country: baseTarget?.name, currency: baseTarget?.currency })}
            </DialogDescription>
          </DialogHeader>
          {saving && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {t('adminPricing.savingHint')}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBaseTarget(null)} disabled={saving}>
              {t('adminPricing.cancel')}
            </Button>
            <Button
              onClick={handleMakeBase}
              disabled={saving}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
              data-testid="make-base-confirm-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t('adminPricing.makeBaseConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rebuild all confirm */}
      <Dialog open={rebuildConfirmOpen} onOpenChange={(o) => !rebuilding && setRebuildConfirmOpen(o)}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>{t('adminPricing.rebuildAllTitle')}</DialogTitle>
            <DialogDescription>{t('adminPricing.rebuildAllDesc')}</DialogDescription>
          </DialogHeader>
          {rebuilding && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {t('adminPricing.savingHint')}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRebuildConfirmOpen(false)} disabled={rebuilding}>
              {t('adminPricing.cancel')}
            </Button>
            <Button
              onClick={() => handleRebuild(false)}
              disabled={rebuilding}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
              data-testid="pricing-rebuild-confirm-btn"
            >
              {rebuilding && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t('adminPricing.rebuildAllConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refresh rates dialog */}
      <Dialog open={fxDialogOpen} onOpenChange={(o) => !(fxLoading || fxApplying) && setFxDialogOpen(o)}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('adminPricing.fxDialogTitle')}</DialogTitle>
            <DialogDescription>{t('adminPricing.fxDialogDesc')}</DialogDescription>
          </DialogHeader>
          {(fxLoading || fxApplying) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {fxApplying ? t('adminPricing.savingHint') : t('adminPricing.fxLoading')}
            </div>
          )}
          {fxResult?.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {fxErrorText(fxResult.error)}
            </div>
          )}
          {fxResult && !fxResult.error && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                {t('adminPricing.fxSources', { sources: (fxResult.sources || []).join(', ') || '—' })}
              </p>
              {(fxResult.results || []).length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">{t('adminPricing.fxNoResults')}</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminPricing.fxColCountry')}</TableHead>
                        <TableHead>{t('adminPricing.currency')}</TableHead>
                        <TableHead className="text-right">{t('adminPricing.fxColOld')}</TableHead>
                        <TableHead className="text-right">{t('adminPricing.fxColNew')}</TableHead>
                        <TableHead className="text-right">{t('adminPricing.fxColChange')}</TableHead>
                        <TableHead>{t('adminPricing.fxColStatus')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fxResult.results.map((r) => {
                        const warn = r.status === 'needs_review' || r.status === 'sources_disagree';
                        const pct = Number(r.changePercent);
                        return (
                          <TableRow key={r.code} className={warn ? 'bg-amber-50' : ''}>
                            <TableCell className="font-mono text-sm">{r.code}</TableCell>
                            <TableCell>{r.currency}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.old == null ? '—' : Number(r.old.toFixed(6))}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.new == null ? '—' : Number(r.new.toFixed(6))}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {Number.isFinite(pct) ? `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
                            </TableCell>
                            <TableCell className={warn ? 'text-amber-800 font-medium' : 'text-gray-700'}>
                              {warn && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                              {fxStatusLabel(r.status)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={fxForce}
                  onChange={(e) => setFxForce(e.target.checked)}
                  disabled={fxApplying}
                  data-testid="fx-force-checkbox"
                />
                <span>
                  {t('adminPricing.fxForce')}
                  <span className="block text-xs text-gray-500">{t('adminPricing.fxForceHint')}</span>
                </span>
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFxDialogOpen(false)} disabled={fxLoading || fxApplying}>
              {t('adminPricing.cancel')}
            </Button>
            <Button
              onClick={handleApplyFx}
              disabled={fxLoading || fxApplying || !fxResult || !!fxResult.error || (fxResult.results || []).length === 0}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
              data-testid="fx-apply-btn"
            >
              {fxApplying && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t('adminPricing.fxApply')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLocationsList;
